import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";

type Props = {
  open: boolean;
  processing?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function ConfirmStartMappingDialog({ open, processing = false, onCancel, onConfirm }: Props) {
  useEffect(() => {
    if (!open || processing) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, processing, onCancel]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-gray-900/20 backdrop-blur-sm"
        onClick={onCancel}
        disabled={processing}
      />
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Start mapping journeys?</h2>
            <p className="mt-2 text-sm text-gray-600">
              We'll sign in as this user and explore signed-in journeys. We may click and fill forms, and that can
              change data. Use a throwaway account or staging environment.
            </p>
          </div>
          <button
            type="button"
            className="cursor-pointer text-gray-400 hover:text-gray-600"
            onClick={onCancel}
            aria-label="Close"
            disabled={processing}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button type="button" className="btn" onClick={onCancel} disabled={processing}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={processing}>
            {processing ? "Saving…" : "Save and start mapping"}
          </button>
        </div>
      </div>
    </div>
  );
}
